import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { AlertTriangle, XCircle, FileText, Shield } from 'lucide-react';

const BanDialog = ({ 
  open, 
  onOpenChange, 
  resident, 
  banData, 
  setBanData, 
  onConfirm, 
  onCancel 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-red-700">
            <XCircle className="h-5 w-5" />
            <span>Ban Resident Form</span>
          </DialogTitle>
          <DialogDescription>
            Create a permanent ban record for {resident?.first_name} {resident?.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning Alert */}
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>Warning:</strong> Banning a resident is a serious action. This will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Immediately remove them from the shelter</li>
                <li>Create a permanent record in their file</li>
                <li>Require administrator approval for any future readmission</li>
                <li>Generate a printable ban notice for records</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Resident Info Summary */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Resident Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-700">Name:</span> {resident?.first_name} {resident?.last_name}
              </div>
              <div>
                <span className="text-gray-700">Intake Date:</span> {resident?.intake_date ? new Date(resident.intake_date).toLocaleDateString() : 'N/A'}
              </div>
              <div>
                <span className="text-gray-700">Stay Length:</span> {resident?.stay_length_days || 0} days
              </div>
              <div>
                <span className="text-gray-700">Room:</span> {resident?.room_id || 'Not Assigned'}
              </div>
            </div>
          </div>

          {/* Ban Details */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span>Reason for Ban *</span>
              </label>
              <Textarea
                required
                value={banData.ban_reason}
                onChange={(e) => setBanData({...banData, ban_reason: e.target.value})}
                placeholder="Enter detailed reason for the ban (required for legal documentation)..."
                rows={4}
                className="border-red-200 focus:border-red-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Be specific and objective. This will appear on official documents.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <FileText className="h-4 w-4" />
                <span>Additional Notes</span>
              </label>
              <Textarea
                value={banData.ban_notes}
                onChange={(e) => setBanData({...banData, ban_notes: e.target.value})}
                placeholder="Additional context, witness information, or relevant details..."
                rows={3}
              />
            </div>
          </div>

          {/* Policy Notice */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900">Shelter Policy</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Banned residents can only be readmitted with administrator approval and completion of 
                  required programs or conditions. A printable ban notice will be generated for your records 
                  and must be kept on file per shelter policies.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={!banData.ban_reason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirm Ban
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BanDialog;